import { useState, type FormEvent } from 'react'
import { Link, Navigate, useNavigate } from 'react-router-dom'
import { ApiError } from '../api/client'
import { useAuth, useLogin } from '../app/auth'
import { BrandMark } from '../app/BrandMark'
import { EmailVerifyForm } from '../onboarding/EmailVerifyForm'

export function LoginPage() {
  const { user, isLoading } = useAuth()
  const navigate = useNavigate()
  const login = useLogin()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [totp, setTotp] = useState('')
  const [needTotp, setNeedTotp] = useState(false)
  const [pendingVerify, setPendingVerify] = useState<{ login: string; email: string | null } | null>(null)

  // Auf "/" leiten (nicht fix /overview) → der HomeRedirect entscheidet die Landung rollenabhängig
  // (Super-Admin → App-Verwaltung, alle anderen → Übersicht).
  if (!isLoading && user) return <Navigate to="/" replace />

  // Pflicht-E-Mail noch nicht verifiziert (Selbstregistrierung): Login lieferte needEmailVerify → Code-Eingabe.
  if (pendingVerify) {
    return <EmailVerifyForm login={pendingVerify.login} email={pendingVerify.email} onVerified={() => navigate('/', { replace: true })} />
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    login.mutate(
      { username, password, totp: needTotp ? totp : undefined },
      {
        onSuccess: (res) => {
          // Passwort ok, aber Konto hat 2FA → in den Code-Schritt wechseln (noch keine Session).
          if ('needTotp' in res && res.needTotp) {
            setNeedTotp(true)
            return
          }
          // Passwort ok, aber Pflicht-E-Mail noch nicht verifiziert → E-Mail-Code-Eingabe (noch keine Session).
          if ('needEmailVerify' in res && res.needEmailVerify) {
            setPendingVerify({ login: username, email: res.email })
            return
          }
          navigate('/', { replace: true })
        },
        onError: () => {
          // Nach falschem/abgelaufenem Code das Feld leeren, damit direkt neu getippt werden kann.
          if (needTotp) setTotp('')
        },
      },
    )
  }

  // Server-Meldung durchreichen (429 Drossel, 401 falsche Daten / falscher oder abgelaufener Code).
  const errorMessage = login.error instanceof ApiError ? login.error.message : login.isError ? 'Anmeldung fehlgeschlagen' : null

  return (
    <div className="centered">
      <form className="card login" onSubmit={onSubmit}>
        <h1 className="wf-lockup">
          <BrandMark size={38} />
          <span>Cevio</span>
        </h1>
        <label className="field">
          <span>Benutzername</span>
          <input
            name="username"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            value={username}
            disabled={needTotp}
            onChange={(e) => setUsername(e.target.value.toLowerCase())}
          />
        </label>
        <label className="field">
          <span>Passwort</span>
          <input
            name="password"
            type="password"
            value={password}
            disabled={needTotp}
            onChange={(e) => setPassword(e.target.value)}
          />
        </label>
        {needTotp && (
          <label className="field">
            <span>Bestätigungscode (2FA)</span>
            <input
              name="totp"
              inputMode="numeric"
              autoComplete="one-time-code"
              autoCorrect="off"
              spellCheck={false}
              // eslint-disable-next-line jsx-a11y/no-autofocus -- Fokus direkt aufs Code-Feld ist hier erwünscht
              autoFocus
              value={totp}
              onChange={(e) => setTotp(e.target.value)}
            />
          </label>
        )}
        {needTotp && (
          <p className="hint">6-stelliger Code aus deiner Authenticator-App – oder ein Recovery-Code.</p>
        )}
        {errorMessage && (
          <p className="error" role="alert">
            {errorMessage}
          </p>
        )}
        <button type="submit" className="btn" disabled={login.isPending}>
          {login.isPending ? 'Anmelden…' : needTotp ? 'Code bestätigen' : 'Anmelden'}
        </button>
        {!needTotp && (
          <nav className="wf-authlinks" aria-label="Onboarding">
            <Link to="/werkstatt-registrieren">Werkstatt registrieren</Link>
            <Link to="/registrieren">Mit Einmal-Code registrieren</Link>
            <Link to="/passwort-vergessen">Passwort vergessen?</Link>
          </nav>
        )}
        <nav className="wf-legalfoot" aria-label="Rechtliches">
          <Link to="/impressum">Impressum</Link>
          <Link to="/datenschutz">Datenschutz</Link>
          <Link to="/avv">AVV</Link>
        </nav>
      </form>
    </div>
  )
}
